import {CimType} from 'frontend/cimclient/graphql/generated/CimSchema';
import {COLORS} from 'frontend/utils/color_palettes';
import {COLOR_THEME} from 'frontend/utils/colors';
import {MapWithDefault} from 'frontend/utils/common';
import {
  batteryGeneratorSVG,
  bioMassGeneratorSVG,
  buildIcon,
  buildIconFromSVG,
  coalGeneratorSVG,
  cspSolarGeneratorSVG,
  dieselGeneratorSVG,
  fuelOilGeneratorSVG,
  generatorSVG,
  geoThermalGeneratorSVG,
  hydroGeneratorSVG,
  locationSvg,
  naturalGasGeneratorSVG,
  photoVoltaicSolarGeneratorSVG,
  pumpedHydroStorageGeneratorSVG,
  transmissionLineSVG,
  windGeneratorSVG,
} from 'frontend/utils/icons';
import {ElectricalTypesIcon} from 'frontend/utils/model/model_analytics';

import {VoltageLevels} from '../../model/voltage_levels';
import {AssetId, MapAsset} from '../map_asset';
import {assetMapMarkersFactory} from '../map_icons';
import {assetMapLinesFactory} from '../map_lines';
import {RenderedObjectPool} from '../rendered_shape_pool';
import {AssetShape, AssetShapeFactory, LINE_COLOR_BY_VOLTAGE_LEVEL} from '../shape_factory';

enum TransmissionLinesIconClassnames {
  EXACT_765 = 'asset-transmission-lines-vl-765',
  RANGE_345_TO_550 = 'asset-transmission-lines-vl-550',
  EXACT_345 = 'asset-transmission-lines-vl-345',
  EXACT_220 = 'asset-transmission-lines-vl-220',
  EXACT_154 = 'asset-transmission-lines-vl-154',
  EXACT_138 = 'asset-transmission-lines-vl-138',
  EXACT_110 = 'asset-transmission-lines-vl-110',
  EXACT_69 = 'asset-transmission-lines-vl-69',
  EXACT_66 = 'asset-transmission-lines-vl-66',
  RANGE_23_TO_44 = 'asset-transmission-lines-vl-44',
  RANGE_4_TO_35 = 'asset-transmission-lines-vl-35',
}

export enum GeneratorTypeIconNames {
  GeneratingUnit = 'asset-generator-unit',
  BatteryStorage = 'asset-generator-battery-storage',
  Biomass = 'asset-generator-biomass',
  HydroPump = 'asset-generator-pumped-hydro',
  HydroPowerPlant = 'asset-generator-hydro',
  FuelOil = 'asset-generator-fuel-oil',
  CSPSolar = 'asset-generator-csv-solar',
  PhotovoltaicSolar = 'asset-generator-photovoltaic-solar',
  Wind = 'asset-generator-wind',
  NaturalGas = 'asset-generator-natural-gas',
  Coal = 'asset-generator-coal',
  Diesel = 'asset-generator-diesel',
  GeoThermal = 'asset-generator-geo-thermal',
  PumpedHydroStorage = 'asset-generator-pumped-hydro-storage',
}

export enum GeneratorTypePureIconNames {
  GeneratingUnit = 'asset-generator-unit',
  BatteryStorage = 'asset-pure-generator-battery-storage',
  Biomass = 'asset-pure-generator-biomass',
  HydroPump = 'asset-pure-generator-pumped-hydro',
  HydroPowerPlant = 'asset-pure-generator-hydro',
  FuelOil = 'asset-pure-generator-fuel-oil',
  CSPSolar = 'asset-pure-generator-csv-solar',
  PhotovoltaicSolar = 'asset-pure-generator-photovoltaic-solar',
  Wind = 'asset-pure-generator-wind',
  NaturalGas = 'asset-pure-generator-natural-gas',
  Coal = 'asset-pure-generator-coal',
  Diesel = 'asset-pure-generator-diesel',
  GeoThermal = 'asset-pure-generator-geo-thermal',
  PumpedHydroStorage = 'asset-pure-generator-pumped-hydro-storage',
}

const DEFAULT_GENERATOR_ICON_SIZE = 18;

export const LOCATION_ICON = buildIconFromSVG(locationSvg());

export const GENERATOR_TYPE_PURE_ICONS = new Map<GeneratorTypePureIconNames, string>([
  [GeneratorTypePureIconNames.GeneratingUnit, generatorSVG({size: DEFAULT_GENERATOR_ICON_SIZE})],
  [
    GeneratorTypePureIconNames.BatteryStorage,
    batteryGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],

  [
    GeneratorTypePureIconNames.Biomass,
    bioMassGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.HydroPump,
    pumpedHydroStorageGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.HydroPowerPlant,
    hydroGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.FuelOil,
    fuelOilGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.CSPSolar,
    cspSolarGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.GeoThermal,
    geoThermalGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.Coal,
    coalGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.Diesel,
    dieselGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.NaturalGas,
    naturalGasGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.Wind,
    windGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.PhotovoltaicSolar,
    photoVoltaicSolarGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
  [
    GeneratorTypePureIconNames.PumpedHydroStorage,
    pumpedHydroStorageGeneratorSVG({
      size: DEFAULT_GENERATOR_ICON_SIZE,
      iconOnly: true,
    }),
  ],
]);

export const GENERATOR_UNIT_LEGEND_ICON_NAME = 'asset-generator-unit-legend';

export const GENERATOR_TYPE_LEGEND_ICONS: Map<string, GeneratorTypeIconNames> = new Map<
  string,
  GeneratorTypeIconNames
>([
  [GeneratorTypeLegendNames.BatteryStorage, GeneratorTypeIconNames.BatteryStorage],
  [GeneratorTypeLegendNames.Biomass, GeneratorTypeIconNames.Biomass],
  [GeneratorTypeLegendNames.Hydro, GeneratorTypeIconNames.HydroPowerPlant],
  [GeneratorTypeLegendNames.FuelOil, GeneratorTypeIconNames.FuelOil],
  [GeneratorTypeLegendNames.CSPSolar, GeneratorTypeIconNames.CSPSolar],
  [GeneratorTypeLegendNames.PVSolar, GeneratorTypeIconNames.PhotovoltaicSolar],
  [GeneratorTypeLegendNames.Wind, GeneratorTypeIconNames.Wind],
  [GeneratorTypeLegendNames.NaturalGas, GeneratorTypeIconNames.NaturalGas],
  [GeneratorTypeLegendNames.Diesel, GeneratorTypeIconNames.Diesel],
  [GeneratorTypeLegendNames.Coal, GeneratorTypeIconNames.Coal],
  [GeneratorTypeLegendNames.GeoThermal, GeneratorTypeIconNames.GeoThermal],
  [GeneratorTypeLegendNames.PumpedHydroStorage, GeneratorTypeIconNames.HydroPump],
]);

export const GENERATOR_TYPE_ICONS = {
  [GeneratorTypeIconNames.GeneratingUnit]: generatorSVG({size: DEFAULT_GENERATOR_ICON_SIZE}),

  [GeneratorTypeIconNames.BatteryStorage]: batteryGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.Biomass]: bioMassGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.HydroPump]: pumpedHydroStorageGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.HydroPowerPlant]: hydroGeneratorSVG({size: DEFAULT_GENERATOR_ICON_SIZE}),
  [GeneratorTypeIconNames.FuelOil]: fuelOilGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.CSPSolar]: cspSolarGeneratorSVG({size: DEFAULT_GENERATOR_ICON_SIZE}),
  [GeneratorTypeIconNames.GeoThermal]: geoThermalGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.Coal]: coalGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.Diesel]: dieselGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.NaturalGas]: naturalGasGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.Wind]: windGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.PhotovoltaicSolar]: photoVoltaicSolarGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
  [GeneratorTypeIconNames.PumpedHydroStorage]: pumpedHydroStorageGeneratorSVG({
    size: DEFAULT_GENERATOR_ICON_SIZE,
  }),
};

const enum GeneratorTypeLegendNames {
  BatteryStorage = 'Battery (storage)',
  Biomass = 'Biomass',
  Hydro = 'Hydro',
  FuelOil = 'Fuel oil',
  CSPSolar = 'CSP solar',
  PVSolar = 'PV solar',
  Wind = 'Wind',
  NaturalGas = 'Natural gas',
  Diesel = 'Diesel',
  Coal = 'Coal',
  GeoThermal = 'Geothermal',
  PumpedHydroStorage = 'Pumped hydro storage (PHS)',
}

export const LINE_ICON_BY_VOLTAGE_LEVEL = new MapWithDefault<VoltageLevels, string>(
  [
    [VoltageLevels.EXACT_765, 'asset-transmission-lines-vl-765'],
    [VoltageLevels.RANGE_345_TO_550, 'asset-transmission-lines-vl-550'],
    [VoltageLevels.EXACT_345, 'asset-transmission-lines-vl-345'],
    [VoltageLevels.EXACT_220, 'asset-transmission-lines-vl-220'],
    [VoltageLevels.EXACT_154, 'asset-transmission-lines-vl-154'],
    [VoltageLevels.EXACT_138, 'asset-transmission-lines-vl-138'],
    [VoltageLevels.EXACT_110, 'asset-transmission-lines-vl-110'],
    [VoltageLevels.EXACT_69, 'asset-transmission-lines-vl-69'],
    [VoltageLevels.EXACT_66, 'asset-transmission-lines-vl-66'],
    [VoltageLevels.RANGE_23_TO_44, 'asset-transmission-lines-vl-44'],
    [VoltageLevels.RANGE_4_TO_35, 'asset-transmission-lines-vl-35'],
  ],
  ElectricalTypesIcon.TRANSMISSION_LINE,
);

export const TRANSMISSION_LINES_ICONS = new Map<string, string>([
  [
    TransmissionLinesIconClassnames.EXACT_765,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_765)),
  ],
  [
    TransmissionLinesIconClassnames.RANGE_345_TO_550,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.RANGE_345_TO_550)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_345,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_345)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_220,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_220)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_154,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_154)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_138,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_138)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_110,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_110)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_69,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_69)),
  ],
  [
    TransmissionLinesIconClassnames.EXACT_66,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.EXACT_66)),
  ],
  [
    TransmissionLinesIconClassnames.RANGE_23_TO_44,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.RANGE_23_TO_44)),
  ],
  [
    TransmissionLinesIconClassnames.RANGE_4_TO_35,
    transmissionLineSVG(LINE_COLOR_BY_VOLTAGE_LEVEL.get(VoltageLevels.RANGE_4_TO_35)),
  ],
]);

/**
 * Factory for creating Google Maps shapes (markers and polylines) for assets.
 */
export class GoogleMapsShapeFactory implements AssetShapeFactory {
  protected assetsPool: RenderedObjectPool<MapAsset, string> | null = null;
  protected readonly multilineTypes = [CimType.AcLineSegment, CimType.EquivalentBranch];

  create(asset: MapAsset, hightlight: boolean, map: google.maps.Map): AssetShape | null {
    if (this.multilineTypes.includes(asset.type)) {
      return assetMapLinesFactory(asset, hightlight);
    } else {
      return assetMapMarkersFactory(asset, hightlight);
    }
  }

  setAssetsPool(assetsPool: RenderedObjectPool<MapAsset, string>) {
    this.assetsPool = assetsPool;
  }

  selectAsset(assetId: AssetId | null) {
    for (const object of this.assetsPool?.objects() || []) {
      object.highlight(!assetId || object.key === assetId, !assetId);
    }
  }
}

/**
 * Creates a Google Maps marker representing a location and adds it to the specified map (if provided).
 */
export function createLocationMarker(
  position: google.maps.LatLng,
  map?: google.maps.Map,
): google.maps.Marker {
  return new google.maps.Marker({
    icon: LOCATION_ICON,
    ...buildLocationMarkerOptions(position, map),
  });
}

/**
 * Builds a set of options for a Google Maps marker representing a location.
 */
export function buildLocationMarkerOptions(
  position: google.maps.LatLng,
  map?: google.maps.Map,
): google.maps.MarkerOptions {
  return {
    position,
    map,
    title: position.toUrlValue(),
  };
}

/** Generates a custom Google Maps icon for a busbar with generator count as badge. */
export function busbarIconGenerator(
  numberOfGeneratorsAttached: number,
  color: string,
  selected = false,
): google.maps.Icon {
  return buildIcon(
    `<style>
       .counter {
         color: ${COLOR_THEME.LIGHT.onSecondaryContainer};
         font-size: 11px;
       }
     </style>
     <circle r="10" cy="14" cx="10" fill="${color}"/>
     <circle r="6" cy="14" cx="10" fill="${selected ? COLORS.NewMagenta.get(60) : 'white'}"/>
     <circle r="6" cy="6" cx="18" fill="${COLOR_THEME.LIGHT.secondaryContainer}"/>
     <text x="${
       numberOfGeneratorsAttached < 10 ? 15.5 : 13
     }" y="9" class="counter">${numberOfGeneratorsAttached}</text>`,
    24,
  );
}
